import { motion } from "framer-motion";
import { DIYA, DIYA_DOTS, NAVY, PINK, YELLOW, CREAM, SPARKLE, marigoldPetals, sunburstPolys } from "@/lib/artPaths";

const H = { "aria-hidden": "true", focusable: "false" };

export function Diya({ lit = true, className = "" }) {
  return (
    <svg viewBox="0 0 240 220" className={className} {...H}>
      <g style={{ transform: lit ? "scale(1)" : "scale(0)", transformOrigin: "120px 104px", transition: "transform .5s cubic-bezier(.2,.9,.3,1.4)" }}>
        <g className={lit ? "flame" : ""}>
          <path d={DIYA.flameOuter} fill={YELLOW} stroke={NAVY} strokeWidth="5" strokeLinejoin="round" />
          <path d={DIYA.flameInner} fill={CREAM} stroke={NAVY} strokeWidth="3" />
        </g>
      </g>
      <path d="M120 100 L120 114" stroke={NAVY} strokeWidth="5" strokeLinecap="round" />
      <path d={DIYA.base} fill={YELLOW} stroke={NAVY} strokeWidth="5" strokeLinejoin="round" />
      <path d={DIYA.bowl} fill={PINK} stroke={NAVY} strokeWidth="5" strokeLinejoin="round" />
      <path d={DIYA.zig} fill="none" stroke={NAVY} strokeWidth="3.5" strokeLinejoin="round" />
      {DIYA_DOTS.map((x) => <circle key={x} cx={x} cy="166" r="5" fill={CREAM} stroke={NAVY} strokeWidth="2.5" />)}
      <path d={DIYA.rim} fill={YELLOW} stroke={NAVY} strokeWidth="5" strokeLinejoin="round" />
    </svg>
  );
}

export function Sunburst({ className = "", color = YELLOW, n = 22 }) {
  return (
    <svg viewBox="-100 -100 200 200" className={className} {...H}>
      {sunburstPolys(n).map((p, i) => <polygon key={i} points={p} fill={color} />)}
    </svg>
  );
}

export function Marigold({ className = "", r = 14 }) {
  return (
    <svg viewBox="-20 -20 40 40" className={className} {...H}>
      {marigoldPetals(r).map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={p.r} fill={i % 2 ? "#F59E0B" : YELLOW} stroke={NAVY} strokeWidth="1.8" />)}
      <circle r={r * 0.34} fill={PINK} stroke={NAVY} strokeWidth="1.8" />
    </svg>
  );
}

export function Sparkle({ className = "", color = YELLOW }) {
  return (
    <svg viewBox="-16 -16 32 32" className={className} {...H}>
      <path d={SPARKLE} fill={color} stroke={NAVY} strokeWidth="2" strokeLinejoin="round" className="twinkle" />
    </svg>
  );
}

export function Bunting({ className = "", width = 1440, flags = 24 }) {
  const step = width / flags;
  const colors = [PINK, YELLOW, CREAM];
  return (
    <svg viewBox={`0 0 ${width} 64`} preserveAspectRatio="xMidYMin slice" className={className} {...H}>
      <path d={`M0 8 Q${width / 2} 22 ${width} 8`} stroke={NAVY} strokeWidth="2.5" fill="none" />
      {Array.from({ length: flags }, (_, i) => {
        const x = i * step + step / 2;
        const y = 8 + 14 * (1 - Math.pow((x - width / 2) / (width / 2), 2));
        return (
          <g key={i} className="sway" style={{ animationDelay: `${(i % 5) * -0.7}s` }}>
            <path d={`M${x - step * 0.38} ${y} L${x + step * 0.38} ${y} L${x} ${y + 38} Z`} fill={colors[i % 3]} stroke={NAVY} strokeWidth="2.5" strokeLinejoin="round" />
          </g>
        );
      })}
    </svg>
  );
}

export function SunDiya({ lit = true, className = "", burst = "settle" }) {
  return (
    <div className={`relative aspect-square ${className}`}>
      <motion.div
        className="absolute inset-[-12%]"
        initial={false}
        animate={burst === "boom" ? { scale: [0.85, 1.25, 1.05], rotate: [0, 18, 12], opacity: 1 } : { scale: lit ? 1 : 0.85, opacity: lit ? 1 : 0.55 }}
        transition={{ duration: burst === "boom" ? 1.1 : 0.6, ease: "easeOut" }}
      >
        <Sunburst className="spin-slow h-full w-full" />
      </motion.div>
      <div className="absolute inset-[26%] rounded-full border-[3px] border-[#142344] bg-[#FFF7E8]" />
      <div className="absolute inset-x-[8%] top-[20%]">
        <Diya lit={lit} className="h-auto w-full drop-shadow-[6px_6px_0_rgba(20,35,68,.9)]" />
      </div>
    </div>
  );
}

export function LogoMark({ className = "" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} {...H}>
      <circle cx="32" cy="32" r="30" fill={YELLOW} stroke={NAVY} strokeWidth="3" />
      <path d="M32 9 C38 18 41 25 38 31 C36 35 28 35 26 31 C23 25 26 18 32 9 Z" fill={CREAM} stroke={NAVY} strokeWidth="2.5" />
      <path d="M10 36 Q32 33 54 36 Q51 52 32 54 Q13 52 10 36 Z" fill={PINK} stroke={NAVY} strokeWidth="3" strokeLinejoin="round" />
      <circle cx="22" cy="44" r="2" fill={CREAM} /><circle cx="32" cy="46" r="2" fill={CREAM} /><circle cx="42" cy="44" r="2" fill={CREAM} />
    </svg>
  );
}
